// Deep compare two objects and return their differences, showing both before and after values
export function diffInObjects<T extends Record<string, unknown>>(
  obj1: T,
  obj2: T,
): Record<string, { oldValue: unknown; newValue: unknown }> {
  const changes: Record<string, { oldValue: unknown; newValue: unknown }> = {}

  const isDate = (value: unknown): value is Date => value instanceof Date

  const areEqual = (val1: unknown, val2: unknown): boolean => {
    // Handle Date comparison
    if (isDate(val1) && isDate(val2)) {
      return val1.getTime() === val2.getTime()
    }
    return val1 === val2
  }

  const compare = (
    o1: Record<string, unknown>,
    o2: Record<string, unknown>,
    path = '',
  ): void => {
    for (const key in o1) {
      const newPath = path ? `${path}.${key}` : key

      // Handle Date objects
      if (isDate(o1[key]) && isDate(o2[key])) {
        if (!areEqual(o1[key], o2[key])) {
          changes[newPath] = { oldValue: o1[key], newValue: o2[key] }
        }
      } else if (
        typeof o1[key] === 'object' &&
        o1[key] !== null &&
        typeof o2[key] === 'object' &&
        o2[key] !== null &&
        !isDate(o1[key]) // Ensure we don't recurse into Date objects
      ) {
        compare(
          o1[key] as Record<string, unknown>,
          o2[key] as Record<string, unknown>,
          newPath,
        )
      } else if (!areEqual(o1[key], o2[key])) {
        changes[newPath] = { oldValue: o1[key], newValue: o2[key] }
      }
    }
    for (const key in o2) {
      if (!(key in o1)) {
        const newPath = path ? `${path}.${key}` : key
        changes[newPath] = { oldValue: undefined, newValue: o2[key] }
      }
    }
  }
  compare(obj1, obj2)
  return changes
}

// Converts a country code to its corresponding flag emoji
export const getCountryFlagEmoji = (countryCode: string | null): string => {
  if (!countryCode) return ''

  // Convert country code to regional indicator symbols
  return Array.from(countryCode.toUpperCase())
    .map(char => String.fromCodePoint(char.charCodeAt(0) + 127397))
    .join('')
}

// Converts a country code to its corresponding region full name
export const getRegionFullName = (countryCode: string | null): string => {
  if (!countryCode) return ''
  try {
    const regionNames = new Intl.DisplayNames(['en'], { type: 'region' })
    return regionNames.of(countryCode) || countryCode
  } catch {
    return countryCode
  }
}

export const formatCurrency = (value: number, currency: string) => {
  if (value === null || value === undefined) return 'N/A'
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency,
  }).format(value)
}
export const isOldApiWebhookVersion = (version: string | null) => {
  if (!version || version === 'unstable') {
    return false
  }
  const [year, month] = version.split('-').map(Number)
  return year < 2024 || (year === 2024 && month <= 7)
}

// Converts Upper underscore case to Capitalized Words. Example: 'SALES_TAX' -> 'Sales Tax'
export function formatUpperUnderscoreCase(type: string): string {
  if (!type) return ''
  return type
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ')
}

// Extract country codes from de minimis group
export function getDeMinimisGroupCountryCodes(group: {
  Countries?: { CountryCode: string }[]
}): string[] {
  return group.Countries?.map(c => c.CountryCode) || []
}

// Get currency symbol from currency code
export const getCurrencySymbol = (currencyCode: string | undefined) => {
  if (!currencyCode) return ''
  try {
    return (
      new Intl.NumberFormat('en', {
        style: 'currency',
        currency: currencyCode.toUpperCase(),
      })
        .formatToParts(0)
        .find(part => part.type === 'currency')?.value || currencyCode
    )
  } catch (error) {
    console.error('Error getting currency symbol:', error)
    return currencyCode
  }
}

/**
 * Build distinct, sorted currency options from a list of objects with a
 * CurrencyCode field (accepts both FilingCountry[] and CurrencyCatalogEntry[]).
 * Each option is shaped { label: 'CAD (CA$)', value: 'CAD' } for PrimeVue Select.
 *
 * Pinned codes (default: USD, EUR) are placed at the top of the list in the
 * order given; remaining codes follow in alphabetical order. Pinned codes that
 * are absent from the input catalog are silently omitted (no duplicates).
 */
export function buildCurrencyOptions(
  countries: { CurrencyCode: string }[],
  pinnedCodes: string[] = ['USD', 'EUR'],
): { label: string; value: string }[] {
  const allCodes = [
    ...new Set(
      countries
        .map(c => c.CurrencyCode?.toString().toUpperCase())
        .filter(Boolean),
    ),
  ]

  const toOption = (code: string) => ({
    label: `${code} (${getCurrencySymbol(code)})`,
    value: code,
  })

  const pinnedUpper = pinnedCodes.map(c => c.toUpperCase())
  const pinned = pinnedUpper.filter(c => allCodes.includes(c)).map(toOption)
  const rest = allCodes
    .filter(c => !pinnedUpper.includes(c))
    .sort()
    .map(toOption)

  return [...pinned, ...rest]
}

// Truncate long IDs to show first 4 and last 4 characters with ellipsis
export const truncateId = (
  id: string,
  firstChars = 4,
  lastChars = 4,
): string => {
  if (!id || id.length <= firstChars + lastChars + 3) return id
  return `${id.slice(0, firstChars)}...${id.slice(-lastChars)}`
}
